/**
 * Warm paper / plum tokens for filter panels while the simple learner theme is on.
 * Avoid `bg-gradient-to*` — the simple theme flattens those fills and leaves white labels unreadable.
 */

export const simpleFilterPanelShellClass =
  'overflow-hidden rounded-xl border border-[#e4d5c3] bg-[#fffdf8] shadow-sm dark:border-[#3d342c] dark:bg-[#1c1714]';

export const simpleFilterHeaderClass =
  'flex items-center justify-between gap-3 border-b border-[#e4d5c3] bg-[#f7f1e8] px-3 py-2 dark:border-[#3d342c] dark:bg-[#261f1b] sm:px-4 sm:py-2.5';

export const simpleFilterIconShellClass =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#3f2a56] text-[#fff8ee] sm:h-8 sm:w-8';

export const simpleFilterTitleClass =
  'text-xs font-semibold text-[#2b221b] dark:text-[#f6efe6] sm:text-sm';

export const simpleFilterSubtitleClass =
  'text-[10px] text-[#8a6a45] dark:text-[#d4b48a]';

export const simpleFilterClearBtnClass =
  'inline-flex shrink-0 items-center gap-1 rounded-lg border border-[#e4d5c3] bg-[#fffdf8] px-2 py-1 text-[11px] font-semibold text-[#3f2a56] shadow-sm transition-colors hover:bg-[#f3e6d4] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#f0d7b0] dark:hover:bg-[#3a2c22] sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs';

export const simpleFilterCountBadgeClass =
  'rounded-full bg-[#3f2a56] px-1.5 py-0.5 text-[9px] font-bold text-[#fff8ee] tabular-nums sm:text-[10px]';

export const simpleFilterIdleBadgeClass =
  'shrink-0 rounded-full border border-[#e4d5c3] bg-[#f3e6d4] px-2 py-0.5 text-[9px] font-medium text-[#6b4b2a] dark:border-[#3d342c] dark:bg-[#3a2c22] dark:text-[#d4b48a] sm:text-[10px]';

export const simpleFilterSearchShellClass =
  'flex min-w-0 items-center gap-1.5 rounded-xl border border-[#e4d5c3] bg-[#fffdf8] px-2 py-1 shadow-sm focus-within:border-[#c4a574] focus-within:ring-2 focus-within:ring-[#c4a574]/40 dark:border-[#3d342c] dark:bg-[#261f1b] dark:focus-within:border-[#d4b48a]/70 dark:focus-within:ring-[#d4b48a]/25';

export const simpleFilterSearchIconClass = 'h-4 w-4 shrink-0 text-[#8a6a45] dark:text-[#d4b48a]';

export const simpleFilterSearchInputClass =
  'w-full border-0 bg-transparent py-1 text-sm text-[#2b221b] outline-none placeholder:text-[#8a6a45]/70 focus:ring-0 dark:text-[#f6efe6] dark:placeholder:text-[#d4b48a]/50';

export const simpleFilterSearchClearClass =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#8a6a45] transition-colors hover:bg-[#f3e6d4] hover:text-[#3f2a56] dark:text-[#d4b48a] dark:hover:bg-[#3a2c22] dark:hover:text-[#fff8ee]';

export const simpleFilterBodyClass =
  'grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5 border-t border-[#e4d5c3] px-2 py-2 dark:border-[#3d342c] sm:gap-x-2.5 sm:gap-y-2 sm:px-3 sm:py-3';

export const simpleFilterReviewShellClass =
  'relative flex items-stretch gap-0.5 overflow-hidden rounded-full border border-[#e4d5c3] bg-[#f7f1e8] p-0.5 dark:border-[#3d342c] dark:bg-[#261f1b]';

export const simpleFilterReviewIdleClass =
  'text-[#6b4b2a] hover:text-[#2b221b] dark:text-[#e6d3bc] dark:hover:text-[#fff8ee]';

export const simpleFilterReviewActiveClass = 'text-[#fff8ee]';

export const simpleFilterReviewActiveMutedClass = 'text-[#2b221b]';

export const simpleFilterIndicatorClass = 'bg-[#3f2a56]';

export const simpleFilterIndicatorReviewedClass = 'bg-[#3d6b4f]';

export const simpleFilterIndicatorMutedClass = 'bg-[#c4a574]';

export const simpleFilterPillTheme = {
  rowClass: '',
  labelIconShellClass:
    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-[#f3e6d4] dark:bg-[#3a2c22] sm:h-6 sm:w-6',
  labelIconClass: 'text-[#6b4b2a] dark:text-[#f0d7b0]',
  labelTextClass:
    'whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-[#8a6a45] dark:text-[#d4b48a]',
  trackShellClass:
    'min-w-0 flex-1 overflow-hidden rounded-full border border-[#e4d5c3] bg-[#f7f1e8] dark:border-[#3d342c] dark:bg-[#261f1b]',
  pillActiveClass: 'text-[#fff8ee]',
  pillInactiveClass:
    'text-[#6b4b2a] hover:text-[#2b221b] dark:text-[#e6d3bc] dark:hover:text-[#fff8ee]',
  scrollBtnClass:
    'border-[#e4d5c3] bg-[#fffdf8] text-[#6b4b2a] hover:bg-[#f3e6d4] dark:border-[#3d342c] dark:bg-[#1c1714] dark:text-[#f0d7b0] dark:hover:bg-[#3a2c22]',
  indicatorClass: 'bg-[#3f2a56]'
} as const;

export const simpleFilterToggleActiveClass =
  'border-[#3f2a56] bg-[#3f2a56] text-[#fff8ee] hover:bg-[#4a3264] dark:border-[#d4b48a] dark:bg-[#3f2a56] dark:text-[#fff8ee]';

export const simpleFilterToggleIdleClass =
  'border-[#e4d5c3] bg-[#fffdf8] text-[#6b4b2a] hover:bg-[#f3e6d4] dark:border-[#3d342c] dark:bg-[#261f1b] dark:text-[#e6d3bc] dark:hover:bg-[#3a2c22]';

/** Count chip on the plum toggle, so it stays readable against the selected button. */
export const simpleFilterToggleCountClass =
  'rounded-full bg-[#fff8ee] px-1.5 py-0.5 text-[9px] font-bold text-[#3f2a56] tabular-nums sm:text-[10px]';
